// ============================================================================
// useOccurrenceHistory — lazy, ref-counted book-page subscription so the
// RecurringOccurrenceChip can render real per-occurrence completion state
// on any surface (Main canvas, Board Explorer, ForYou, Member surfaces).
// ----------------------------------------------------------------------------
// Why this exists: the projected `ChecklistItem.occurrences` map that
// checklistViewAdapter builds only carries the anchor page's completion —
// on non-recurring surfaces that's today; on the Recurring canvas that's
// the focused day. So on Main / BoardExplorer / ForYou, the chip's
// history dropdown showed every past/future row as UN-completed even
// when the underlying page actually had `isCompleted: true`.
//
// Design points:
//   1. The hook takes `enabled` and only registers a listener while true.
//      Chip callers pass `enabled = isOpen` so subscriptions cost nothing
//      until a user opens the popover.
//   2. Listeners are keyed by bookId and reference-counted. N chip
//      instances on the same book share a single Firestore listener
//      (relevant on ForYou / BoardExplorer where dozens of chips render
//      at once). Last unsubscribe tears down the Firestore listener.
//   3. Returned history is a memoized `Record<pageKey, {isCompleted,
//      workflowStatus, completedAt, completedById, completedByName}>` —
//      the shape the chip already knows how to render.
//   4. Returns `null` while the subscription hasn't emitted its first
//      snapshot yet — callers fall back to `item.occurrences` in the
//      meantime.
// ============================================================================

import { useEffect, useSyncExternalStore } from 'react'
import { bookIdFor, type BookId, type ChecklistBookPage } from '../../types/checklistBooks'
import { subscribeToBookPages } from '../../services/checklistBookService'
import type { RecurringMode } from '../../types/recurringChecklist'
import type { ChecklistWorkflowStatus, RecurringOccurrenceState } from '../../types/schema'

type OccurrenceHistory = Record<string, RecurringOccurrenceState>

type BookEntry = {
  refCount: number
  unsubscribe: () => void
  pages: ChecklistBookPage[] | null
  subscribers: Set<() => void>
  /** Per-checklistId cache of the projected history, invalidated on
   *  every pages update. `useSyncExternalStore` requires getSnapshot to
   *  return referentially-stable values when data hasn't changed;
   *  building the projection fresh every call would loop React forever
   *  ("The result of getSnapshot should be cached to avoid an infinite
   *  loop"). */
  histories: Map<string, OccurrenceHistory | null>
}

// Module-scoped so N hook instances on the same book share a listener.
const cache = new Map<BookId, BookEntry>()

const acquireBook = (bookId: BookId): BookEntry => {
  const existing = cache.get(bookId)
  if (existing) {
    existing.refCount += 1
    return existing
  }
  const entry: BookEntry = {
    refCount: 1,
    unsubscribe: () => {},
    pages: null,
    subscribers: new Set(),
    histories: new Map(),
  }
  cache.set(bookId, entry)
  entry.unsubscribe = subscribeToBookPages(bookId, (pages) => {
    entry.pages = pages
    entry.histories.clear() // fresh pages → drop stale projections
    // Fire every subscriber's onStoreChange so useSyncExternalStore
    // pulls the fresh snapshot.
    for (const notify of entry.subscribers) notify()
  })
  return entry
}

const releaseBook = (bookId: BookId): void => {
  const entry = cache.get(bookId)
  if (!entry) return
  entry.refCount -= 1
  if (entry.refCount <= 0) {
    entry.unsubscribe()
    cache.delete(bookId)
  }
}

const EMPTY_HISTORY: OccurrenceHistory = Object.freeze({})

/** Project + memoize the target checklist's completion state across every
 *  page. Cached per (bookId, checklistId) on the BookEntry so repeated
 *  getSnapshot calls with unchanged pages return the same reference. */
const historyFor = (entry: BookEntry, checklistId: string): OccurrenceHistory | null => {
  if (!entry.pages) return null
  if (entry.histories.has(checklistId)) {
    return entry.histories.get(checklistId) ?? null
  }
  const history: OccurrenceHistory = {}
  for (const page of entry.pages) {
    const completion = page.completions?.[checklistId]
    if (!completion) continue
    history[page.pageKey] = {
      isCompleted: Boolean(completion.isCompleted),
      workflowStatus: (completion.workflowStatus as ChecklistWorkflowStatus | null) ?? null,
      completedAt: completion.completedAt ?? null,
      completedById: completion.completedById ?? null,
      completedByName: completion.completedByName ?? null,
    }
  }
  entry.histories.set(checklistId, history)
  return history
}

export type UseOccurrenceHistoryInput = {
  itemId: string
  workspaceId: string | null | undefined
  mode: RecurringMode
  /** Gate the subscription — the chip passes `isOpen` so no listener
   *  attaches until the user actually opens the history popover. */
  enabled: boolean
}

export type UseOccurrenceHistoryResult = {
  /** Map of pageKey → completion state. Null while the first snapshot is
   *  in flight (callers should fall back to the projected occurrences). */
  history: OccurrenceHistory | null
  /** True until the first snapshot has landed. */
  isLoading: boolean
}

/** Subscribe to the book's pages (ref-counted globally) while `enabled`
 *  is true and return the target checklist's per-page completion entries. */
export const useOccurrenceHistory = (
  input: UseOccurrenceHistoryInput,
): UseOccurrenceHistoryResult => {
  const { itemId, workspaceId, mode, enabled } = input

  useEffect(() => {
    if (!enabled || !workspaceId) return
    const bookId = bookIdFor(workspaceId, mode)
    acquireBook(bookId)
    return () => releaseBook(bookId)
  }, [enabled, workspaceId, mode])

  const bookId = enabled && workspaceId ? bookIdFor(workspaceId, mode) : null

  const subscribe = (onStoreChange: () => void) => {
    if (!bookId) return () => {}
    const entry = cache.get(bookId)
    if (!entry) return () => {}
    entry.subscribers.add(onStoreChange)
    return () => entry.subscribers.delete(onStoreChange)
  }

  const getSnapshot = (): OccurrenceHistory | null => {
    if (!bookId) return null
    const entry = cache.get(bookId)
    if (!entry) return null
    return historyFor(entry, itemId)
  }

  const getServerSnapshot = () => null

  const history = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
  return {
    history: history ?? (enabled ? null : EMPTY_HISTORY),
    isLoading: enabled && history === null,
  }
}

/** Test-only: reset the module-scoped cache between vitest cases. */
export const __resetOccurrenceHistoryCache = () => {
  for (const entry of cache.values()) entry.unsubscribe()
  cache.clear()
}
